import { useState, useMemo } from 'react';

const BASE_PRICE = 158.40;

const STRIKE_DATA = [
  { strike: 145, delta: 0.05 },
  { strike: 147, delta: 0.09 },
  { strike: 149, delta: 0.13 },
  { strike: 151, delta: 0.18 },
  { strike: 153, delta: 0.24 },
  { strike: 155, delta: 0.30 },
];

function getEntryPremium(strike, ivRank, delta) {
  const ivMult = 0.5 + ivRank / 100;
  return +Math.max(0.05, strike * delta * 0.18 * ivMult).toFixed(2);
}

function getCurrentPremium(strike, entryPremium, currentPrice, daysLeft) {
  if (currentPrice < strike) {
    // ITM: intrinsic + time value
    const intrinsic = strike - currentPrice;
    const timeValue = entryPremium * Math.sqrt(daysLeft / 45) * 0.25;
    return +Math.max(intrinsic, intrinsic + timeValue).toFixed(2);
  }
  // OTM: premium × sqrt(daysLeft/45) × (1 - distance × 3)
  const distance = (currentPrice - strike) / currentPrice;
  const value = entryPremium * Math.sqrt(daysLeft / 45) * Math.max(0, 1 - distance * 3);
  return +Math.max(0.01, value).toFixed(2);
}

function evaluateSetup({ trend, rsi, ivRank, earnings }) {
  const pros = [], issues = [];

  if (trend === 'bullish') pros.push('Bullish trend supports put selling');
  if (trend === 'bearish') issues.push('Bearish trend increases assignment risk');
  if (trend === 'sideways') pros.push('Sideways market — reduced directional risk');

  if (rsi >= 40 && rsi <= 65) pros.push('RSI neutral — good entry zone');
  else if (rsi > 65 && rsi <= 70) pros.push('RSI bullish momentum');
  else if (rsi > 30 && rsi < 40) issues.push('RSI approaching oversold — wait for bounce confirmation');
  else if (rsi <= 30) issues.push('RSI oversold — momentum is bearish. Wait for RSI to turn up above 30 before entering. Do not catch a falling knife.');
  else if (rsi > 70) issues.push('RSI overbought — pullback risk');

  if (ivRank >= 50) pros.push(`IV Rank ${ivRank} — elevated premium`);
  else if (ivRank >= 30) pros.push(`IV Rank ${ivRank} — premium favorable`);
  else issues.push(`IV Rank ${ivRank} — premium too low`);

  if (earnings === 'before') issues.push('Earnings pending — binary risk');
  if (earnings === 'after') pros.push('Post-earnings IV crush opportunity');

  const doNotEnter = trend === 'bearish' || earnings === 'before' || rsi < 25;
  const strongSetup = trend === 'bullish' && rsi >= 35 && rsi <= 65 && ivRank >= 30 && earnings !== 'before';

  let grade, color;
  if (doNotEnter) {
    grade = 'Do Not Enter';
    color = '#ef4444';
  } else if (strongSetup) {
    grade = 'Strong Setup';
    color = '#22c55e';
  } else {
    grade = 'Borderline — Tradeable with Caution';
    color = '#eab308';
  }

  return { pros, issues, grade, color };
}

const generateCandles = (trend) => {
  const candles = [];

  const makeCandle = (price, targetPrice, noiseAmt) => {
    const drift = (targetPrice - price) * 0.25;
    const noise = (Math.random() - 0.5) * price * noiseAmt;
    const open = price;
    const close = price + drift + noise;
    const body = Math.abs(close - open);
    const high = Math.max(open, close) + body * 0.4 + price * 0.002;
    const low = Math.min(open, close) - body * 0.4 - price * 0.002;
    return {
      open: parseFloat(open.toFixed(2)),
      high: parseFloat(high.toFixed(2)),
      low: parseFloat(low.toFixed(2)),
      close: parseFloat(close.toFixed(2)),
      bull: close >= open,
    };
  };

  if (trend === 'bullish') {
    const bullishPrices = [
      148.2, 149.1, 149.8, 150.6,
      150.2, 151.1, 151.8, 152.5,
      152.1, 153.0, 153.8, 154.5,
      154.1, 155.0, 155.8, 156.4,
      156.0, 156.9, 157.6, 158.3,
      157.9, 158.7, 159.4, 160.1,
      159.7, 160.5, 161.2, 161.8,
      161.4, 162.2, 162.9, 163.5,
      163.1, 163.9, 164.5, 165.0,
      164.7, 165.3, 165.8, 166.2,
      165.9, 166.4, 166.8, 167.1,
      166.8, 166.2, 165.7, 165.2,
      165.5, 165.2, 164.8, 165.1,
      164.7, 165.0, 164.6, 165.2,
      164.9, 165.3, 164.8, 165.1,
      164.7, 165.0, 164.8,
    ];
    for (let i = 0; i < bullishPrices.length; i++) {
      const open = i === 0 ? bullishPrices[0] : bullishPrices[i - 1];
      const close = bullishPrices[i];
      const body = Math.abs(close - open);
      const high = Math.max(open, close) + body * 0.3 + close * 0.001;
      const low = Math.min(open, close) - body * 0.3 - close * 0.001;
      candles.push({
        open: parseFloat(open.toFixed(2)),
        high: parseFloat(high.toFixed(2)),
        low: parseFloat(low.toFixed(2)),
        close: parseFloat(close.toFixed(2)),
        bull: close >= open,
      });
    }
  } else if (trend === 'bearish') {
    const START = 168, END = 148;
    let price = START;
    for (let i = 0; i < 60; i++) {
      const targetPrice = START + (END - START) * (i / 59);
      const c = makeCandle(price, targetPrice, 0.006);
      candles.push(c);
      price = c.close;
    }
  } else {
    let price = 156;
    for (let i = 0; i < 60; i++) {
      const c = makeCandle(price, 158, 0.010);
      candles.push(c);
      price = c.close;
    }
  }

  return candles;
};

function PriceChart({ currentPrice, strikes, entryPrice, trend, candles, selectedStrike, tradeEntered }) {
  const W = 600;
  const CHART_H = 220;
  const RSI_H = 60;
  const TOTAL_H = CHART_H + RSI_H + 16;

  const allPrices = [
    ...candles.flatMap(c => [c.high, c.low]),
    currentPrice || 158.40,
  ];

  if (selectedStrike) {
    allPrices.push(selectedStrike);
  }

  if (entryPrice) {
    allPrices.push(entryPrice);
  }

  const minPrice = Math.min(...allPrices) * 0.994;
  const maxPrice = Math.max(...allPrices) * 1.006;
  const py = price => CHART_H - ((price - minPrice) / (maxPrice - minPrice)) * CHART_H;

  const candleW = W / candles.length;
  const cw = candleW;
  const bodyW = Math.max(2, candleW * 0.7);

  const markerY = Math.min(CHART_H - 6, Math.max(6, py(currentPrice)));

  const closes = candles.map(c => c.close);
  const sma = (period, endIdx) => {
    const slice = closes.slice(Math.max(0, endIdx - period + 1), endIdx + 1);
    if (slice.length === 0) return null;
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  };
  const ma20Points = candles.map((_, i) => sma(20, i)).filter(v => v !== null);
  const ma50Points = candles.map((_, i) => sma(50, i)).filter(v => v !== null);
  const ma200Points = candles.map((_, i) => sma(200, i)).filter(v => v !== null);

  const maToPoints = (maArray) =>
    maArray.map((v, i) => `${i * cw + cw / 2},${py(v)}`).join(' ');

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${TOTAL_H}`} style={{ display: 'block' }}>
      <rect width={W} height={TOTAL_H} fill="#0d1117" rx="8" />
      <text x={8} y={16}
        fill={trend === 'bullish' ? '#7aab8a' : trend === 'bearish' ? '#c47a7a' : '#c9a84c'}
        fontSize="10" fontFamily="monospace" fontWeight="bold">
        {trend === 'bullish' ? '▲ BULLISH' : trend === 'bearish' ? '▼ BEARISH' : '→ SIDEWAYS'}
      </text>
      {strikes.map(s => (
        <line key={s.strike} x1={0} y1={py(s.strike)} x2={W} y2={py(s.strike)}
          stroke="#1e3a5f" strokeDasharray="4,4" strokeWidth="1" />
      ))}
      {entryPrice && (
        <line x1={0} y1={py(entryPrice)} x2={W} y2={py(entryPrice)}
          stroke="#f59e0b" strokeDasharray="6,3" strokeWidth="1.5" />
      )}
      {candles.map((c, i) => {
        const cx = i * candleW + candleW / 2;
        const bodyTop = py(Math.max(c.open, c.close));
        const bodyBot = py(Math.min(c.open, c.close));
        const bodyH = Math.max(2, bodyBot - bodyTop);
        const col = c.bull ? '#00e599' : '#ff4d6d';
        return (
          <g key={i}>
            <line x1={cx} y1={py(c.high)} x2={cx} y2={py(c.low)} stroke={col} strokeWidth="1" />
            <rect x={cx - bodyW / 2} y={bodyTop} width={bodyW} height={bodyH} fill={col} />
          </g>
        );
      })}
      {ma20Points.length > 1 && (
        <polyline points={maToPoints(ma20Points)} fill="none" stroke="#4a9eff" strokeWidth="1.5" opacity="0.85" />
      )}
      {ma50Points.length > 1 && (
        <polyline points={maToPoints(ma50Points)} fill="none" stroke="#7aab8a" strokeWidth="1.2" opacity="0.85" />
      )}
      {ma200Points.length > 1 && (
        <polyline points={maToPoints(ma200Points)} fill="none" stroke="#c47a7a" strokeWidth="1" opacity="0.8" />
      )}
      {tradeEntered && (() => {
        const entryX = (candles.length - 1) * candleW + candleW / 2;
        const lastCandle = candles[candles.length - 1];
        return (
          <g>
            <line x1={entryX} x2={entryX} y1={0} y2={CHART_H}
              stroke="#c9a84c" strokeWidth="1" strokeDasharray="4,3" opacity="0.5" />
            <polygon
              points={`${entryX},${py(lastCandle.low) + 14} ${entryX - 5},${py(lastCandle.low) + 24} ${entryX + 5},${py(lastCandle.low) + 24}`}
              fill="#c9a84c" />
            <text x={entryX} y={py(lastCandle.low) + 36}
              fill="#c9a84c" fontSize="8" fontFamily="monospace" textAnchor="middle" fontWeight="bold">
              ENTRY
            </text>
            <line x1={0} x2={W} y1={py(158.40)} y2={py(158.40)}
              stroke="#c9a84c" strokeWidth="1" strokeDasharray="3,2" opacity="0.4" />
            <text x={4} y={py(158.40) - 3}
              fill="rgba(201,168,76,0.6)" fontSize="8" fontFamily="monospace">
              ENTRY $158.40
            </text>
          </g>
        );
      })()}
      <line x1={0} y1={py(currentPrice)} x2={W} y2={py(currentPrice)}
        stroke="#60a5fa" strokeDasharray="3,3" strokeWidth="1" opacity="0.6" />
      <circle cx={W - 6} cy={markerY} r="4" fill="#60a5fa" />
      <text x={W - 90} y={markerY - 5} fill="#60a5fa" fontSize="11">
        ${currentPrice.toFixed(2)}
      </text>
      <rect x={4} y={CHART_H - 30} width={160} height={26} fill="rgba(13,17,23,0.85)" rx="3" />
      {[['#4a9eff', 'MA20'], ['#7aab8a', 'MA50'], ['#c47a7a', 'MA200']].map(([color, label], i) => (
        <g key={label}>
          <line x1={8 + i * 54} x2={22 + i * 54} y1={CHART_H - 17} y2={CHART_H - 17} stroke={color} strokeWidth="2" />
          <text x={26 + i * 54} y={CHART_H - 13} fill={color} fontSize="8" fontFamily="monospace">{label}</text>
        </g>
      ))}
      {trend === 'bearish' && (
        <g>
          <rect x={W - 164} y={4} width={160} height={28} fill="rgba(13,17,23,0.88)" rx="3" />
          <text x={W - 84} y={16} fill="#c9a84c" fontSize="9" fontFamily="monospace" textAnchor="middle" fontWeight="bold">Bear Call Spread Territory</text>
          <text x={W - 84} y={27} fill="rgba(201,168,76,0.6)" fontSize="8" fontFamily="monospace" textAnchor="middle">Chapter 7</text>
        </g>
      )}
      {trend === 'bullish' && !tradeEntered && (() => {
        const lastIdx = candles.length - 1;
        const lastX = lastIdx * candleW + candleW / 2;
        const ma20Val = sma(20, lastIdx);
        if (!ma20Val) return null;
        const labelY = py(ma20Val);
        return (
          <g>
            <rect x={lastX - 168} y={labelY - 30} width={162} height={26} fill="rgba(13,17,23,0.82)" rx="3" />
            <text x={lastX - 8} y={labelY - 17}
              fill="#c9a84c" fontSize="9" fontFamily="monospace" textAnchor="end" fontWeight="bold">
              ← Testing 20 MA Support
            </text>
            <text x={lastX - 8} y={labelY - 6}
              fill="rgba(201,168,76,0.55)" fontSize="8" fontFamily="monospace" textAnchor="end">
              Watch for bounce confirmation
            </text>
          </g>
        );
      })()}
    </svg>
  );
}

const S = {
  page: { background: '#0d1117', color: '#e2e8f0', minHeight: '100vh', padding: '16px', fontFamily: 'system-ui,sans-serif', fontSize: '13px' },
  card: { background: '#111827', border: '1px solid #1e2535', borderRadius: '8px', padding: '14px' },
  label: { fontSize: '11px', color: '#64748b', marginBottom: '3px' },
  h2: { fontSize: '13px', fontWeight: 700, marginBottom: '10px', color: '#cbd5e1' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' },
  slider: { width: '100%', accentColor: '#3b82f6', cursor: 'pointer' },
  btn: (on, col = '#475569') => ({
    padding: '5px 11px', borderRadius: '4px', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: on ? 700 : 400,
    background: on ? col : '#111827', color: on ? '#fff' : '#64748b',
    border: `1px solid ${on ? col : '#1e2535'}`,
  }),
};

export default function CSPSimulatorV2({ onNavigate, hideHeader }) {
  const [conditions, setConditions] = useState({ trend: 'bullish', rsi: 45, ivRank: 55, earnings: 'none' });
  const [selectedStrike, setSelectedStrike] = useState(null);
  const [tradeEntered, setTradeEntered] = useState(null);
  const [priceOffset, setPriceOffset] = useState(0);
  const [daysElapsed, setDaysElapsed] = useState(0);
  const [showDebrief, setShowDebrief] = useState(false);
  const [enteredAgainstRules, setEnteredAgainstRules] = useState(false);

  const currentPrice = BASE_PRICE + priceOffset;
  const daysLeft = Math.max(0, 45 - daysElapsed);
  const eval_ = evaluateSetup(conditions);
  const candles = useMemo(() => generateCandles(conditions.trend), [conditions.trend]);

  const strikes = STRIKE_DATA.map(s => {
    const ep = getEntryPremium(s.strike, conditions.ivRank, s.delta);
    return {
      ...s,
      entryPremium: ep,
      currentPremium: tradeEntered ? getCurrentPremium(s.strike, ep, currentPrice, daysLeft) : null,
    };
  });

  const sel = selectedStrike ? strikes.find(s => s.strike === selectedStrike) : null;
  const activePnl = tradeEntered && sel
    ? +((tradeEntered.premium - sel.currentPremium) * 100).toFixed(2)
    : null;

  const handleEnter = () => {
    if (!sel) return;
    setTradeEntered({ strike: sel.strike, premium: sel.entryPremium, enteredAt: BASE_PRICE });
    setPriceOffset(0);
    setDaysElapsed(0);
  };

  const handleEnterAnyway = () => {
    if (!sel) return;
    setTradeEntered({ strike: sel.strike, premium: sel.entryPremium, enteredAt: BASE_PRICE });
    setEnteredAgainstRules(true);
    setPriceOffset(0);
    setDaysElapsed(0);
  };

  const handlePreset = (dp, days) => { setPriceOffset(+dp.toFixed(2)); setDaysElapsed(days); };

  const handleExit = () => setShowDebrief(true);

  const handleCloseDebrief = () => {
    setShowDebrief(false);
    setTradeEntered(null);
    setSelectedStrike(null);
    setPriceOffset(0);
    setDaysElapsed(0);
    setEnteredAgainstRules(false);
  };

  const cSet = (key, val) => setConditions(c => ({ ...c, [key]: val }));

  return (
    <div style={S.page}>
      {/* Header */}
      {!hideHeader && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#f1f5f9' }}>CSP Simulator V2</div>
          <div style={{ fontSize: '13px', color: tradeEntered ? '#22c55e' : '#94a3b8' }}>
            {tradeEntered ? `Trade Active — $${sel?.strike} Put` : 'No Position'}
          </div>
        </div>
      )}

      {/* Chart */}
      <div style={{ ...S.card, marginBottom: '12px', padding: '10px' }}>
        <PriceChart currentPrice={currentPrice} strikes={strikes} entryPrice={tradeEntered?.enteredAt} trend={conditions.trend} candles={candles} selectedStrike={selectedStrike} tradeEntered={!!tradeEntered} />
      </div>

      {/* Bullish educational card */}
      {conditions.trend === 'bullish' && !tradeEntered && (
        <div style={{ ...S.card, marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', fontFamily: 'Jost, system-ui, sans-serif', lineHeight: 1.8 }}>
            Price is testing the 20 MA as support — a common CSP entry zone.<br />
            Wait for a bullish candle to confirm the bounce before entering your position.
          </div>
        </div>
      )}

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>

        {/* LEFT: Conditions */}
        <div style={S.card}>
          <div style={S.h2}>Market Conditions</div>

          {/* Trend */}
          <div style={{ marginBottom: '12px' }}>
            <div style={S.label}>Trend Direction</div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[['bullish','#22c55e'],['sideways','#eab308'],['bearish','#ef4444']].map(([t, c]) => (
                <button key={t} style={S.btn(conditions.trend === t, c)}
                  onClick={() => cSet('trend', t)}>
                  {t[0].toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* RSI */}
          <div style={{ marginBottom: '12px' }}>
            <div style={S.row}>
              <div style={S.label}>RSI</div>
              <div style={{ fontSize: '11px', color: conditions.rsi > 70 ? '#ef4444' : conditions.rsi < 30 ? '#22c55e' : '#94a3b8' }}>
                {conditions.rsi}{conditions.rsi > 70 ? ' overbought' : conditions.rsi < 30 ? ' oversold' : ''}
              </div>
            </div>
            <input type="range" min="10" max="90" value={conditions.rsi} style={S.slider}
              onChange={e => cSet('rsi', +e.target.value)} />
          </div>

          {/* IV Rank */}
          <div style={{ marginBottom: '12px' }}>
            <div style={S.row}>
              <div style={S.label}>IV Rank</div>
              <div style={{ fontSize: '11px', color: conditions.ivRank >= 50 ? '#22c55e' : conditions.ivRank < 30 ? '#ef4444' : '#eab308' }}>
                {conditions.ivRank}% — {conditions.ivRank >= 50 ? 'high' : conditions.ivRank < 30 ? 'low' : 'medium'}
              </div>
            </div>
            <input type="range" min="0" max="100" value={conditions.ivRank} style={S.slider}
              onChange={e => cSet('ivRank', +e.target.value)} />
          </div>

          {/* Earnings */}
          <div style={{ marginBottom: '12px' }}>
            <div style={S.label}>Earnings</div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[['none','None','#475569'],['before','Before','#ef4444'],['after','After','#22c55e']].map(([v,l,c]) => (
                <button key={v} style={S.btn(conditions.earnings === v, c)}
                  onClick={() => cSet('earnings', v)}>{l}</button>
              ))}
            </div>
          </div>

          {/* Evaluation */}
          {conditions.trend === 'bearish' ? (
            <>
              <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.22)', borderRadius: '6px', padding: '12px', marginBottom: '8px' }}>
                <div style={{ fontWeight: 700, color: '#ef4444', fontSize: '12px', marginBottom: '8px' }}>
                  Do Not Enter — CSP
                </div>
                <div style={{ fontWeight: 700, color: '#fca5a5', fontSize: '11px', marginBottom: '6px' }}>
                  Wrong Strategy for These Conditions
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.8 }}>
                  A bearish trend with overbought RSI is not a CSP setup.<br /><br />
                  Never sell cash secured puts when price is trending down. Even with elevated IV — the premium is not worth the assignment risk.<br /><br />
                  <span style={{ color: '#fca5a5' }}>✗ Bearish trend — stock declining</span><br />
                  <span style={{ color: '#fca5a5' }}>✗ RSI overbought at {conditions.rsi} — momentum already stretched to the upside and turning</span><br /><br />
                  <span style={{ color: '#cbd5e1' }}>What these conditions ARE good for:</span><br />
                  A Bear Call Spread profits when price stays below your short call strike — exactly what a downtrend supports.<br /><br />
                  <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>This strategy unlocks after Chapter 7.</span>
                </div>
              </div>
              <div style={{ background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.22)', borderRadius: '6px', padding: '12px', marginBottom: '8px' }}>
                <div style={{ fontSize: '11px', color: '#e2c97e', lineHeight: 1.8 }}>
                  Come back to these conditions after Chapter 7 — Covered Calls &amp; Bear Call Spread.<br /><br />
                  You will see why this setup that looks wrong for a CSP is actually ideal for a different strategy.
                </div>
              </div>
              {onNavigate && (
                <button
                  onClick={() => onNavigate('chapter_ch07')}
                  style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '6px', color: '#c9a84c', fontSize: '11px', padding: '7px 12px', cursor: 'pointer', width: '100%' }}>
                  Continue to Chapter 7 →
                </button>
              )}
            </>
          ) : (
            <div style={{ background: '#1a2236', borderRadius: '6px', padding: '10px' }}>
              <div style={{ fontWeight: 700, color: eval_.color, marginBottom: '6px', fontSize: '12px' }}>
                {eval_.grade}
              </div>
              {eval_.pros.map((p, i) => (
                <div key={i} style={{ fontSize: '11px', color: '#22c55e', marginBottom: '2px' }}>✓ {p}</div>
              ))}
              {eval_.issues.map((p, i) => (
                <div key={i} style={{ fontSize: '11px', color: '#ef4444', marginBottom: '2px' }}>✗ {p}</div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: Options Chain */}
        <div style={S.card}>
          <div style={S.h2}>Options Chain — 45 DTE Puts</div>

          {/* Chain rows */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '4px',
              padding: '4px 8px', fontSize: '10px', color: '#475569', marginBottom: '4px' }}>
              <span>Strike</span><span>Delta</span><span>Premium</span>
              <span>{tradeEntered ? 'Current' : 'PoP'}</span>
            </div>
            {strikes.map(row => {
              const itm = row.strike > currentPrice;
              const picked = selectedStrike === row.strike;
              return (
                <div key={row.strike}
                  onClick={() => !tradeEntered && setSelectedStrike(row.strike)}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '4px',
                    padding: '7px 8px', marginBottom: '3px', borderRadius: '4px',
                    cursor: tradeEntered ? 'default' : 'pointer',
                    background: picked ? '#1a2236' : itm ? '#2d1515' : '#111827',
                    border: `1px solid ${picked ? 'rgba(201,168,76,0.35)' : itm ? '#7f1d1d' : '#1e2535'}`,
                  }}>
                  <span style={{ color: itm ? '#fca5a5' : '#e2e8f0', fontWeight: picked ? 700 : 400 }}>
                    ${row.strike}
                  </span>
                  <span style={{ color: '#64748b' }}>{row.delta.toFixed(2)}δ</span>
                  <span style={{ color: '#22c55e' }}>${row.entryPremium.toFixed(2)}</span>
                  {tradeEntered && row.currentPremium !== null ? (
                    <span style={{ color: row.currentPremium < row.entryPremium ? '#22c55e' : '#ef4444' }}>
                      ${row.currentPremium.toFixed(2)}
                    </span>
                  ) : (
                    <span style={{ color: '#475569' }}>{(100 - row.delta * 100).toFixed(0)}%</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Trade math */}
          {sel && (
            <div style={{ background: '#1a2236', borderRadius: '6px', padding: '10px', marginBottom: '10px' }}>
              <div style={S.h2}>Trade Math</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', fontSize: '12px' }}>
                <div style={{ color: '#64748b' }}>Strike</div>
                <div style={{ color: '#60a5fa' }}>${sel.strike}</div>
                <div style={{ color: '#64748b' }}>Premium</div>
                <div style={{ color: '#22c55e' }}>${sel.entryPremium} /share</div>
                <div style={{ color: '#64748b' }}>Credit</div>
                <div style={{ color: '#22c55e' }}>${(sel.entryPremium * 100).toFixed(0)}</div>
                <div style={{ color: '#64748b' }}>Collateral</div>
                <div style={{ color: '#94a3b8' }}>${(sel.strike * 100).toFixed(0)}</div>
                <div style={{ color: '#64748b' }}>Breakeven</div>
                <div style={{ color: '#eab308' }}>${(sel.strike - sel.entryPremium).toFixed(2)}</div>
                <div style={{ color: '#64748b' }}>Return</div>
                <div style={{ color: '#22c55e' }}>{((sel.entryPremium / sel.strike) * 100).toFixed(2)}%</div>
              </div>
            </div>
          )}

          {/* Enter / P&L */}
          {!tradeEntered ? (
            <>
              <button
                onClick={handleEnter}
                disabled={!sel || eval_.grade === 'Do Not Enter'}
                style={{
                  width: '100%', padding: '10px', borderRadius: '6px', border: 'none',
                  cursor: (sel && eval_.grade !== 'Do Not Enter') ? 'pointer' : 'not-allowed',
                  fontWeight: 700, fontSize: '13px',
                  background: (sel && eval_.grade !== 'Do Not Enter') ? '#15803d' : '#1a2236',
                  color: (sel && eval_.grade !== 'Do Not Enter') ? '#fff' : '#475569',
                }}>
                {sel ? `Sell $${sel.strike} Put for $${sel.entryPremium}` : 'Select a Strike to Trade'}
              </button>
              {sel && eval_.grade === 'Do Not Enter' && (
                <>
                  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '6px', padding: '10px', marginTop: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#ef4444', marginBottom: '6px' }}>No Valid Setup</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '6px' }}>What needs to change:</div>
                    {eval_.issues.map((issue, i) => (
                      <div key={i} style={{ fontSize: '11px', color: '#fca5a5', marginBottom: '2px' }}>• {issue}</div>
                    ))}
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '8px', fontStyle: 'italic' }}>Patience is a trading skill.</div>
                  </div>
                  <button
                    onClick={handleEnterAnyway}
                    style={{
                      width: '100%', padding: '7px', borderRadius: '6px', marginTop: '6px',
                      background: 'transparent', border: '1px solid rgba(239,68,68,0.25)',
                      color: 'rgba(239,68,68,0.7)', fontSize: '11px', cursor: 'pointer',
                    }}>
                    Enter Anyway — See What Happens →
                  </button>
                </>
              )}
            </>
          ) : (
            <div style={{ background: '#1a2236', borderRadius: '6px', padding: '10px' }}>
              <div style={S.row}>
                <span style={{ color: '#64748b' }}>P&L</span>
                <span style={{ fontSize: '18px', fontWeight: 700, color: activePnl >= 0 ? '#22c55e' : '#ef4444' }}>
                  {activePnl >= 0 ? '+' : ''}{activePnl?.toFixed(2)}
                </span>
              </div>
              <div style={{ fontSize: '11px', color: '#475569', marginTop: '4px' }}>
                Entry ${tradeEntered.premium.toFixed(2)} → Now ${sel?.currentPremium?.toFixed(2)} · {daysLeft}d left
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trade management (after entering) */}
      {tradeEntered && !showDebrief && (
        <div style={S.card}>
          {enteredAgainstRules && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '10px', marginBottom: '12px' }}>
              <span style={{ fontSize: '12px', color: '#fca5a5' }}>⚠ You entered against the rules. Watch what happens.</span>
            </div>
          )}
          {/* Sliders */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '14px' }}>
            <div>
              {(() => {
                const change = currentPrice - BASE_PRICE;
                const changePct = (change / BASE_PRICE) * 100;
                const bep = sel ? sel.strike - tradeEntered.premium : null;
                const aboveStrike = sel && currentPrice > sel.strike;
                const aboveBep = sel && bep !== null && currentPrice > bep && currentPrice <= sel.strike;
                const pillLabel = !sel ? null : aboveStrike ? 'ABOVE STRIKE' : aboveBep ? 'BELOW STRIKE' : 'BELOW BREAKEVEN';
                const pillColor = !sel ? '#475569' : aboveStrike ? '#7aab8a' : aboveBep ? '#c9a84c' : '#f43f5e';
                const pillBg = !sel ? 'transparent' : aboveStrike ? 'rgba(122,171,138,0.12)' : aboveBep ? 'rgba(201,168,76,0.12)' : 'rgba(244,63,94,0.12)';
                const minPx = BASE_PRICE - 15;
                const maxPx = BASE_PRICE + 15;
                const minPct = Math.round((minPx - BASE_PRICE) / BASE_PRICE * 100);
                const maxPct = Math.round((maxPx - BASE_PRICE) / BASE_PRICE * 100);
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '8px' }}>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: '#c9a84c', fontFamily: 'monospace', lineHeight: 1 }}>
                        ${currentPrice.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '13px', color: change >= 0 ? '#22c55e' : '#ef4444' }}>
                        {change >= 0 ? '+' : ''}${Math.abs(change).toFixed(2)} ({change >= 0 ? '+' : ''}{changePct.toFixed(1)}%)
                      </div>
                      {sel && (
                        <div style={{ fontSize: '10px', fontWeight: 700, color: pillColor, background: pillBg, border: `1px solid ${pillColor}`, borderRadius: '12px', padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                          ● {pillLabel}
                        </div>
                      )}
                    </div>
                    <input type="range" min="-15" max="15" step="0.5" value={priceOffset} style={S.slider}
                      onChange={e => setPriceOffset(+e.target.value)} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px' }}>
                      <div style={{ fontSize: '10px', color: '#475569' }}>${minPx.toFixed(0)} ({minPct}%)</div>
                      <div style={{ fontSize: '10px', color: '#c9a84c' }}>${BASE_PRICE.toFixed(0)} Entry</div>
                      <div style={{ fontSize: '10px', color: '#475569' }}>${maxPx.toFixed(0)} (+{maxPct}%)</div>
                    </div>
                  </>
                );
              })()}
            </div>
            <div>
              <div style={S.row}>
                <div style={S.label}>Time Elapsed</div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {daysElapsed}d passed · {daysLeft}d left
                </div>
              </div>
              <input type="range" min="0" max="45" value={daysElapsed} style={S.slider}
                onChange={e => setDaysElapsed(+e.target.value)} />
            </div>
          </div>

          {/* Presets */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
            {[
              { label: 'Next Week', dp: 0.8, days: 7 },
              { label: 'Gap Down 5%', dp: -(BASE_PRICE * 0.05), days: 3 },
              { label: 'Earnings Drop', dp: -(BASE_PRICE * 0.08), days: 1 },
              { label: 'Expiry Eve', dp: 0, days: 44 },
            ].map(p => (
              <button key={p.label} style={S.btn(false)}
                onClick={() => handlePreset(p.dp, p.days)}>{p.label}</button>
            ))}
          </div>

          {/* Greek cards */}
          {sel && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px', marginBottom: '14px' }}>
              {[
                { name: 'Delta', val: `${(-sel.delta).toFixed(3)}`, desc: 'Price sensitivity', col: '#60a5fa' },
                { name: 'Theta', val: `+$${(sel.entryPremium / 45).toFixed(3)}`, desc: 'Daily decay earned', col: '#22c55e' },
                { name: 'Gamma', val: `${(sel.delta * 0.05).toFixed(4)}`, desc: 'Delta acceleration', col: '#eab308' },
                { name: 'Vega', val: `${(sel.entryPremium * 0.1).toFixed(3)}`, desc: 'IV sensitivity', col: '#a78bfa' },
              ].map(g => (
                <div key={g.name} style={{ background: '#1a2236', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', color: '#475569', marginBottom: '4px' }}>{g.name}</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: g.col, marginBottom: '3px' }}>{g.val}</div>
                  <div style={{ fontSize: '10px', color: '#334155' }}>{g.desc}</div>
                </div>
              ))}
            </div>
          )}

          {/* Emotional awareness */}
          <div style={{ background: '#1a2236', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
            <div style={S.h2}>Emotional Awareness</div>
            <div style={{ fontSize: '12px', marginBottom: '6px' }}>
              {activePnl > 50 ? (
                <span style={{ color: '#22c55e' }}>Profit zone — resist closing early. Let theta keep working.</span>
              ) : activePnl < -150 ? (
                <span style={{ color: '#ef4444' }}>Significant loss — was your thesis invalidated? Stick to predefined exits.</span>
              ) : activePnl < 0 ? (
                <span style={{ color: '#eab308' }}>Small loss — normal fluctuation. Is delta within your risk threshold?</span>
              ) : (
                <span style={{ color: '#60a5fa' }}>Breakeven range — theta is accumulating. Monitor strike vs. price.</span>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#334155' }}>
              Ask: Am I managing by plan or by emotion?
            </div>
          </div>

          <button onClick={handleExit} style={{
            width: '100%', padding: '10px', borderRadius: '6px', border: 'none',
            cursor: 'pointer', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: '13px',
          }}>
            Close Position
          </button>
        </div>
      )}

      {/* Debrief modal */}
      {showDebrief && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}>
          <div style={{ ...S.card, maxWidth: '400px', width: '90%' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px', color: '#f1f5f9' }}>
              Trade Debrief
            </div>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#94a3b8' }}>
              Closed ${tradeEntered?.strike} put — Final P&L:{' '}
              <span style={{ fontWeight: 700, color: activePnl >= 0 ? '#22c55e' : '#ef4444' }}>
                {activePnl >= 0 ? '+' : ''}{activePnl?.toFixed(2)}
              </span>
            </div>
            {enteredAgainstRules && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '6px', padding: '10px', marginBottom: '10px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#ef4444', marginBottom: '5px' }}>You entered when conditions were not favorable.</div>
                <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.6 }}>Here is what the rules were protecting you from.</div>
              </div>
            )}
            <div style={{ background: '#1a2236', borderRadius: '6px', padding: '10px', marginBottom: '14px' }}>
              <div style={{ fontWeight: 700, color: '#cbd5e1', marginBottom: '8px', fontSize: '12px' }}>
                Reflection Questions
              </div>
              {[
                'Did you follow your entry criteria before entering?',
                'Did emotion influence any decisions mid-trade?',
                'Was your exit based on your plan or on fear/greed?',
                'What would you do differently next time?',
              ].map((q, i) => (
                <div key={i} style={{ fontSize: '12px', color: '#64748b', marginBottom: '5px' }}>• {q}</div>
              ))}
            </div>
            <button onClick={handleCloseDebrief} style={{
              width: '100%', padding: '10px', borderRadius: '6px', border: 'none',
              cursor: 'pointer', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: '13px',
            }}>
              Start New Trade
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
